import {
  useState, useCallback, useContext,
} from 'react';
import { LoadingContext } from 'context/Loading';
import handlePromise from 'utils/Promise';
import { ModalErrorContext } from 'context/ModalError';
import DocsRequestService from 'services/api/DocsRequest';
import { b64toBlob } from 'utils/downloadBase64';

const useDownloadDocRequest = () => {
  const { toggleLoading } = useContext( LoadingContext );
  const { openErrorModal } = useContext( ModalErrorContext );

  const [showFileModal, setShowFileModal] = useState( false );
  const [currentFileUrl, setCurrentFileUrl] = useState<string | null>( null );
  const [fileUrls, setFileUrls] = useState<Record<string, string>>( {} );

  const openFileModal = useCallback( () => {
    setShowFileModal( true );
  }, [] );

  const closeFileModal = useCallback( () => {
    setShowFileModal( false );
    setCurrentFileUrl( null );
  }, [] );

  const handleViewDocument = useCallback(
    async (
      documentoId: number,
      cuentaDeInversionID: number,
      url: string,
      nombreArchivo: string,
      tipoArchivo: string,
      descargar = false,
    ) => {
      if ( fileUrls[documentoId] ) {
        const blobUrl = fileUrls[documentoId];

        if ( descargar ) {
          const blob = await fetch( blobUrl ).then( ( r ) => r.blob() );
          const a = document.createElement( 'a' );
          a.href = URL.createObjectURL( blob );
          a.download = `${nombreArchivo}`;
          a.click();
          URL.revokeObjectURL( a.href );
        } else {
          setCurrentFileUrl( blobUrl );
          openFileModal();
        }
        return;
      }

      toggleLoading( true );
      const [, response, dataFile] = await handlePromise(
        DocsRequestService.postFileByUrl( cuentaDeInversionID, url ),
      );
      toggleLoading( false );

      if ( !response.ok || !dataFile.includes( 'base64,' ) ) {
        openErrorModal( 'No se pudo obtener el documento. Intente nuevamente más tarde.' );
        return;
      }

      const base64 = dataFile.split( 'base64,' )[1];
      if ( !base64 || base64.length < 1000 ) {
        openErrorModal( 'El documento está dañado o vacío. No se puede visualizar.' );
        return;
      }

      const mime = tipoArchivo === '.pdf' ? 'application/pdf' : `image/${tipoArchivo.replace( '.', '' )}`;
      const blob = b64toBlob( base64, mime );
      const blobUrl = URL.createObjectURL( blob );

      setFileUrls( ( prev ) => ( { ...prev, [documentoId]: blobUrl } ) );

      if ( descargar ) {
        const a = document.createElement( 'a' );
        a.href = blobUrl;
        a.download = `${nombreArchivo}`;
        a.click();
      } else {
        setCurrentFileUrl( blobUrl );
        openFileModal();
      }
    },
    [fileUrls],
  );

  const clearCachedFile = useCallback( ( documentoId: number ) => {
    setFileUrls( ( prev ) => {
      if ( prev[documentoId] ) {
        URL.revokeObjectURL( prev[documentoId] );
        const newState = { ...prev };
        delete newState[documentoId];
        return newState;
      }
      return prev;
    } );
  }, [] );

  return {
    showFileModal,
    currentFileUrl,
    closeFileModal,
    handleViewDocument,
    clearCachedFile,
  };
};

export default useDownloadDocRequest;
